<template>
	<div class="box">
		<div class="box-header">
			<h2 class="box-title">Bulk Data Entry</h2>
		</div>
		<div class="box-body">
			<fieldset>
				<a class="fa fa-question-circle popover-help pull-right"
					role="button"
					tabindex="0"
					data-trigger="focus"
					data-container="body"
					data-placement="left"
					data-html="true"
					title="Help"
					data-content="
					<small>
						Temperature and Baume data can be entered directly into the data table below. To record actions performed on the ferment, lab analysis results and other comments, click on the Action/Lab Analysis/Comments section in the desired row to bring up a box where all of this information can be entered against the selected time point. Actions will be displayed in the table by the icons shown below.<br />
						<br />
						<div class='row'>
							<div class='col-xs-6'>
								<i class='fa fa-fw fa-sort-amount-asc'></i> - Pumpover<br />
								<i class='fa fa-fw fa-spinner'></i> - Aeration<br />
								<i class='fa fa-fw fa-refresh'></i> - Agitator<br />
								<i class='fa fa-fw fa-plus-square'></i> - DAP<br />
							</div>
							<div class='col-xs-6'>
								<i class='fontello-beer-1'></i> - Juice<br />
								<i class='fa fa-fw fa-tint'></i> - Water<br />
								<i class='fa fa-fw fa-flask'></i> - Acid<br />
								<i class='fa fa-fw fa-chain-broken'></i> - Overseed
							</div>
						</div>
					</small>
					">
				</a>

				<table class="table table-profile table-daily-data" cellspacing="0">
					<thead>
						<tr>
							<th class="col-fermenter">Fermenter</th>
							<th class="col-ferment">Ferment Name</th>
							<th class="col-day">Day</th>
							<th class="col-baume" v-if="allowBaume">Sugar ({{{ units.sugarUnit() }}})</th>
							<th class="col-temp">Temp ({{{ units.temperatureUnit() }}})</th>
							<th class="col-secondary">{{{ units.secondarySugarUnit() }}}</th>
							<th class="col-action">Action/Analysis</th>
							<th class="col-comments">Comments</th>
						</tr>

						<tr v-for="fermenter in filteredFermenters">
							<td>{{ fermenter.tankGroup.data.name }} - {{ fermenter.name }}</td>
							<td>{{ fermenter.currentFerment.data.wine_name }}</td>
							<td>{{ fermenter.currentFerment.data.currentDay.day }}</td>
							<td @click.stop="editCell($event, $index, 'baume', fermenter.currentFerment.data.currentDay)" class="baume" :class="{'info': editing.status && editing.index == $index && editing.column == 'baume'}">
								<div v-if="editing.status && editing.index == $index && editing.column == 'baume'">
									<input type="text" @keydown.tab="tabToNextCell($event, $index, 'temp', 'comments', fermenter.currentFerment.data.currentDay)" v-model="fermenter.currentFerment.data.currentDay.baume" class="form-control"></input>
								</div>
								<div style="width:100%" v-else>
									{{fermenter.currentFerment.data.currentDay.baume ? parseFloat(fermenter.currentFerment.data.currentDay.baume).toFixed(2) : fermenter.currentFerment.data.currentDay.baume}} {{{fermenter.currentFerment.data.currentDay.baume ? units.sugarUnit() : '' }}}
								</div>
							</td>
							<td @click.stop="editCell($event, $index, 'temp', fermenter.currentFerment.data.currentDay)" class="temp" :class="{'info': editing.status && editing.index == $index && editing.column == 'temp'}">
								<div v-if="editing.status && editing.index == $index && editing.column == 'temp'">
									<input type="text" @keydown.tab="tabToNextCell($event, $index, 'secondary', 'baume', fermenter.currentFerment.data.currentDay)" v-model="fermenter.currentFerment.data.currentDay.temp" class="form-control"></input>
								</div>
								<div style="width:100%" v-else>
									{{fermenter.currentFerment.data.currentDay.temp ? parseFloat(fermenter.currentFerment.data.currentDay.temp).toFixed(2) : fermenter.currentFerment.data.currentDay.temp}} {{{fermenter.currentFerment.data.currentDay.temp ? units.temperatureUnit() : '' }}}
								</div>
							</td>
							<td @click.stop="editCell($event, $index, 'secondary', fermenter.currentFerment.data.currentDay)" class="secondary" :class="{'info': editing.status && editing.index == $index && editing.column == 'secondary'}">
								<div v-if="editing.status && editing.index == $index && editing.column == 'secondary'">
									<input type="text" @keydown.tab="tabToNextCell($event, $index, 'comments', 'temp', fermenter.currentFerment.data.currentDay)" v-model="fermenter.currentFerment.data.currentDay.analysis.data[simulatorSetup.secondary_sugar_units]" class="form-control"></input>
								</div>
								<div style="width:100%" v-else>
									<span v-if="simulatorSetup.secondary_sugar_units == 'rs'">
										{{fermenter.currentFerment.data.currentDay.analysis.data.rs || fermenter.currentFerment.data.currentDay.analysis.data.rs === 0 ? parseFloat(fermenter.currentFerment.data.currentDay.analysis.data.rs).toFixed(2) : fermenter.currentFerment.data.currentDay.analysis.data.rs}} {{{fermenter.currentFerment.data.currentDay.analysis.data.rs || fermenter.currentFerment.data.currentDay.analysis.data.rs === 0 ? 'g/L' : '' }}}
									</span>
									<span v-else>
										{{fermenter.currentFerment.data.currentDay.analysis.data.sg || fermenter.currentFerment.data.currentDay.analysis.data.sg === 0 ? parseFloat(fermenter.currentFerment.data.currentDay.analysis.data.sg).toFixed(4) : fermenter.currentFerment.data.currentDay.analysis.data.sg}}
									</span>
								</div>
							</td>
							<td class="action-modal" @click="openActionModal($index)">
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.pump_over" class="fa fa-fw fa-sort-amount-asc"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.aeration" class="fa fa-fw fa-spinner"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.agitator > 0" class="fa fa-fw fa-refresh"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.dap" class="fa fa-fw fa-plus-square"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.juice" class="fontello-beer-1"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.water" class="fa fa-fw fa-tint"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.acid" class="fa fa-fw fa-flask"></i>
								<i v-if="fermenter.currentFerment.data.currentDay.action.data && fermenter.currentFerment.data.currentDay.action.data.reinoculate" class="fa fa-fw fa-chain-broken"></i>
								<i v-if="!isEmptyAnalysis(fermenter.currentFerment.data.currentDay)" class="fa fa-table"></i>
							</td>
							<td @click.stop="editCell($event, $index, 'comments', fermenter.currentFerment.data.currentDay)" class="comments"  :class="{'info': editing.status && editing.index == $index && editing.column == 'comments'}">
								<div v-if="editing.status && editing.index == $index && editing.column == 'comments'">
									<input type="text" class="form-control" @keydown.tab="tabToNextCell($event, $index, 'baume', 'secondary', fermenter.currentFerment.data.currentDay)" v-model="fermenter.currentFerment.data.currentDay.comments"></input>
								</div>
								<div v-else>
									{{fermenter.currentFerment.data.currentDay.comments}}
								</div>
							</td>
						</tr>

						<tr v-if="filteredFermenters.length == 0">
							<td colspan="7" align="center">
								There are no fermenters for the current day.
							</td>
						</tr>
					</thead>
				</table>

				<div class="modal fade" id="actions_analysis" tabindex="-1" role="dialog" aria-labelledby="actionsTitle" aria-hidden="true" v-if="editing.index != null">
					<div class="modal-dialog">
						<div class="modal-content">
							<div class="modal-header">
								<button type="button" class="close" data-dismiss="modal"><span aria-hidden="true">&times;</span><span class="sr-only">Close</span></button>
								<h4 class="modal-title" id="actionsTitle">Actions and Analysis</h4>
							</div>
							<div class="modal-body">
								<h2 class="no-margin-top">Actions</h2>
								<div class="modal-actions">
									<div class="row">
										<div class="col-xs-6">
											<input-toggle
												name="type"
												:vmodel.sync="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.pump_over"
												on="Performed"
												off="None"
												on-style="primary"
												label="Pump Over"
												icon="sort-amount-asc">
											</input-toggle>
										</div>
										<div class="col-xs-6">
											<input-toggle
												name="type"
												:vmodel.sync="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.aeration"
												on="Performed"
												off="None"
												on-style="primary"
												label="Aeration"
												icon="spinner">
											</input-toggle>
										</div>
									</div>
									<div class="row">
										<div class="col-sm-12">
											<div class="form-group form-control-toggle-top">
												<label for="agitator" class="col-xs-12 control-label text-left"><i class="fa fa-refresh"></i> Agitator</label>
												<div class="col-xs-12">
													<div class="btn-group" id="agitator-group" data-toggle="buttons" v-radio="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.agitator" default-value="0">
														<label class="btn btn-default active">
															<input type="radio" value="0"> Off
														</label>
														<label class="btn btn-default">
															<input type="radio" value="1"> 1
														</label>
														<label class="btn btn-default">
															<input type="radio" value="2"> 2
														</label>
														<label class="btn btn-default">
															<input type="radio" value="3"> 3
														</label>

														<agitator-definition></agitator-definition>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-sm-6">
											<div class="form-group">
												<label for="add_dap" class="col-xs-12 control-label text-left"><i class="fa fa-plus-square"></i> Add DAP</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="add_dap" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.dap"/>
														<span class="input-group-addon input-group-addon-narrow">mg/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="add_juice" class="col-xs-12 control-label text-left"><i class="fontello-beer-1"></i> Add juice</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="add_juice" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.juice"/>
														<span class="input-group-addon input-group-addon-narrow">L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="add_water" class="col-xs-12 control-label text-left"><i class="fa fa-tint"></i> Add Water</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="add_water" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.water" />
														<span class="input-group-addon input-group-addon-narrow">L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="add_acid" class="col-xs-12 control-label text-left"><i class="fa fa-flask"></i> Add Acid</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="add_acid" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.acid"/>
														<span class="input-group-addon input-group-addon-narrow">L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="add_acid" class="col-xs-12 control-label text-left">
													<i class="fa fa-chain-broken"></i> Overseed

													<a class="fa fa-question-circle popover-help" style="margin-left: 7px"
														tabindex="0"
														role="button"
														data-placement="left"
														data-html="true"
														data-trigger="focus"
														data-container="body"
														title="Help"
														data-content="
														<small style='margin-bottom: 10px; display: block'>
															If propagating, you can use the following equation to convert your cell concentration:
														</small>
														<small style='margin-bottom: 10px; display: block'>
															<code>Cell conc (cells/ml) x pitch rate (decimal) x 5x10^-5</code>
														</small>
														<small style='margin-bottom: 10px; display: block'>
															For example:
														</small>
														<small>
															<code>Equivalent dry yeast = 1x10^8 x 0.05 x 5x10^-5 = 250mg/L</code>
														</small>">
													</a>
												</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="add_acid" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.action.data.reinoculate"/>
														<span class="input-group-addon input-group-addon-narrow">mg/L</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<h2>Lab Analysis</h2>
								<div class="modal-actions">
									<div class="row">
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_cell_count" class="col-xs-12 control-label text-left">Cell Count</label>
												<div class="col-xs-12">
													<input type="text" name="ana_cell_count" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.cell_count">
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_ph" class="col-xs-12 control-label text-left">pH</label>
												<div class="col-xs-12">
													<input type="text" name="ana_ph" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.ph">
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_ta" class="col-xs-12 control-label text-left">TA</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_ta" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.ta">
														<span class="input-group-addon input-group-addon-narrow">g/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_fs02" class="col-xs-12 control-label text-left">FS0<sub>2</sub></label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_fs02" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.fs02">
														<span class="input-group-addon input-group-addon-narrow">mg/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_ts02" class="col-xs-12 control-label text-left">TS0<sub>2</sub></label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_ts02" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.ts02">
														<span class="input-group-addon input-group-addon-narrow">mg/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_gf" class="col-xs-12 control-label text-left">G/F</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_gf" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.gf">
														<span class="input-group-addon input-group-addon-narrow">g/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_ntu" class="col-xs-12 control-label text-left">NTU</label>
												<div class="col-xs-12">
													<input type="text" name="ana_ntu" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.ntu">
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_alcohol" class="col-xs-12 control-label text-left">Alcohol</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_alcohol" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.alcohol">
														<span class="input-group-addon input-group-addon-narrow">%</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group">
												<label for="ana_malic_acid" class="col-xs-12 control-label text-left">Malic Acid</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_malic_acid" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.malic_acid">
														<span class="input-group-addon input-group-addon-narrow">g/L</span>
													</div>
												</div>
											</div>
										</div>
										<div class="col-sm-6">
											<div class="form-group" v-if="simulatorSetup.secondary_sugar_units == 'rs'">
												<label for="ana_malic_acid" class="col-xs-12 control-label text-left">SG</label>
												<div class="col-xs-12">
													<input type="text" name="ana_sg" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.sg">
												</div>
											</div>
											<div class="form-group" v-else>
												<label for="ana_malic_acid" class="col-xs-12 control-label text-left">RS</label>
												<div class="col-xs-12">
													<div class="input-group">
														<input type="text" name="ana_sg" class="form-control input-sm" v-model="filteredFermenters[editing.index].currentFerment.data.currentDay.analysis.data.rs">
														<span class="input-group-addon input-group-addon-narrow">g/L</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="modal-footer">
								<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>
							</div>
						</div>
					</div>
				</div>

			</fieldset>
		</div>
	</div>
</template>

<script>
	import units from 'utils/units'
	import tabbing from 'components/ferment/daily-data/tabbing'
	import rowHelpers from 'components/ferment/daily-data/row-helpers'
	import dayHelpers from 'components/ferment/daily-data/day-helpers'
	import AgitatorDefinition from 'components/ferment/agitator-definition'
	import moment from 'moment'
	import { site, simulatorSetup } from 'vx/getters'
	import { Ferments as FermentActions } from 'vx/actions'
	import Notify from 'utils/notify'
	import Vue from 'vue'

	export default {
		components: {
			AgitatorDefinition
		},

		data() {
			return {
				editing: {
					status: false,
					index: null,
					column: null
				},
				allowBaume: true,
				filteredFermenters: [],
				timeouts: {},
				watchers: {},
				preventUpdate: false,
				units
			}
		},

		vuex: {
			getters: {
				site,
				simulatorSetup
			},

			actions: {
				updateFerment: FermentActions.update,
				getFerment: FermentActions.get,
				runSimulation: FermentActions.simulation
			}
		},

		props: ['fermenters', 'date', 'search', 'period', 'selectedTankGroup'],

		watch: {
			date() {
				this.filterFermenters();
			},

			selectedTankGroup() {
				this.filterFermenters();
			},

			search() {
				this.filterFermenters();
			},

			period() {
				this.filterFermenters();
			}
		},

		created() {
			this.filterFermenters();
		},

		methods: {
			...tabbing,
			...rowHelpers,
			...dayHelpers,

			/**
			 * Handle tabbing to a new row.
			 *
			 * @return void
			 */
			handleTabToBlankRow(event, index, previous, next) {
				this.editing = {
					status: true,
					index: 0,
					column: 'baume'
				};
			},

			/**
			 * Find the next row. This will take us back to the top.
			 *
			 * @param object row
			 *
			 * @return object
			 */
			findNextRow(row) {
				return $('.table-daily-data').find('tr:nth-child(2)');
			},

			/**
			 * Get the next day.
			 *
			 * We don't actually set this here, we'll set it after we generate the
			 * new blank day in the filterFermenters method.
			 *
			 * @return null
			 */
			getNextDay() {
				return null;
			},

			filterFermenters() {
				this.filteredFermenters = this.fermenters.filter(fermenter => {
					let shouldBeIncluded = true;

					if (! fermenter.currentFerment) {
						shouldBeIncluded = false;
					} else {
						let ferment = fermenter.currentFerment.data;
						let inoculatedAtPM = _.endsWith(ferment.start_date, 'PM');

						// We start by getting the difference in days between the selected date and
						// fermenters start date. We'll use this to get the current day from the
						// daily data.
						let currentDay = moment(this.date, 'DD/MM/YYYY').diff(moment(ferment.start_date, 'DD/MM/YYYY hh:mm A').startOf('day'), 'days');

						// We need to take into account ferments that start at a PM time slot. If
						// it also has a measuring frequency of 0.5 and we are not on the PM
						// side of the toggle then we need to subtract 0.5 from the current
						// day. This prevents PM inoculated ferments from showing in the
						// bulk data list if the period toggle is not set to PM.
						if (inoculatedAtPM && ferment.measuring_freq == 0.5 && ! this.period) {
							currentDay -= 0.5;
						}

						if (currentDay < 0) {
							return false;
						}

						let index = _.findIndex(ferment.dailyData.data, day => day.day == currentDay);

						if (index == -1) {
							index = ferment.dailyData.data.length;
						}

						// If we are on the PM of the period toggle and this ferment has a
						// measuring frequency of 0.5 AND it was not inoculated in the
						// afternoon then we need to increment the index and the
						// current day so we get the afternoon information.
						if (this.period && ferment.measuring_freq == 0.5 && ! inoculatedAtPM) {
							index++;

							currentDay += 0.5;
						}

						let dayData;

						// If we don't have an entry for the index in the daily data we'll need to
						// generate a new blank day and then assign the current day we calculated.
						if (! ferment.dailyData.data[index]) {
							dayData = this.newBlankDay();

							dayData.day = currentDay;
						} else {
							dayData = ferment.dailyData.data[index];
						}

						// Now we can determine if we have a baume entry for the current day.
						// If we do not then we can add the current day data to the
						// current ferment so that we can show it in the table.
						if (dayData && dayData.baume) {
							shouldBeIncluded = false;
						} else {
							fermenter.currentFerment.data = $.extend({}, fermenter.currentFerment.data, { 'currentDay': dayData });
						}

					}

					return shouldBeIncluded;
				});

				// Reset any watchers we have by calling them as functions, this unsets
				// them, then we'll remove them all by resetting the variable.
				for (let fermenterId in this.watchers) {
					this.watchers[fermenterId]();
				}

				this.watchers = {};

				// For each of the filtered fermenters we'll setup a new watcher that
				// will update it whenever a change is made.
				this.filteredFermenters.map(fermenter => {
					this.watchers[fermenter.id] = this.$watch(() => fermenter, () => {
						clearTimeout(this.timeouts[fermenter.id]);

						if (this.preventUpdate) {
							this.preventUpdate = false;

							return;
						}

						this.timeouts[fermenter.id] = setTimeout(() => {
							this.updateFermenter(fermenter);
						}, 1500);
					}, { deep: true });
				});
			},

			updateFermenter(fermenter) {
				let data = $.extend(true, {}, fermenter.currentFerment.data);

				data.start_date = moment(data.start_date, 'DD/MM/YYYY hh:mm A').format('YYYY-MM-DD HH:mm:ss');

				// Update the units to convert them to celsius and baume as that is what
				// they are being saved in the database as.
				data.dailyData.data.forEach(day => {
					day.temp = units.fahrenheitToCelsius(day.temp);
					day.baume = units.brixToBaume(day.baume);
				});

				if (data.juiceAnalysis) {
					data.juiceAnalysis.data.baume = units.brixToBaume(data.juiceAnalysis.data.baume);
				}

				let lastDay;

				if (data.dailyData.data.length > 0) {
					lastDay = data.dailyData.data[data.dailyData.data.length - 1];
				}

				let daysToAdd = lastDay ? (data.currentDay.day - (lastDay.day + fermenter.currentFerment.data.measuring_freq)) : data.currentDay.day,
					newDay;

				for (let i = data.currentDay.day - daysToAdd; i < data.currentDay.day; i += fermenter.currentFerment.data.measuring_freq) {
					newDay = this.newBlankDay();

					newDay.day = i;

					data.dailyData.data.push(newDay);
				}

				// Before we remove the current day from the data we need to make sure that
				// the current day exists within the daily data. If it does not we'll need
				// to add it.
				let index = _.findIndex(data.dailyData.data, day => day.day == data.currentDay.day);

				if (! data.dailyData.data[index]) {
					data.dailyData.data.push(data.currentDay);
				} else {
					data.dailyData.data[index] = data.currentDay;
				}

				this.updateFerment(this.site.id, fermenter.id, data).then(response => {
					this.getFerment(
						this.site.id,
						fermenter.id,
						fermenter.currentFerment.data.id,
						['juiceAnalysis', 'dailyData.action', 'dailyData.analysis']
					).then(response => {
						this.preventUpdate = true;

						fermenter.currentFerment.data.dailyData = response.body.data.dailyData;

						Notify.success('Ferment daily data has been updated.');
					});
				});
			},

			openActionModal(index) {
				this.editing.index = index;

				Vue.nextTick(() => {
					$('#actions_analysis').modal().on('hidden.bs.modal', () => {
						this.editing.index = null;
					});
				});
			}
		}
	}
</script>



// WEBPACK FOOTER //
// bulk-entry.vue?222e7002